/**
 * getPaymentStatus - Derives payment status from existing request + booking data.
 * No new backend calls or schema changes — purely derived logic.
 *
 * @param {Object} inquiry - The inquiry/request object
 * @param {Object|null} booking - The associated booking object (from getBookingForInquiry)
 * @returns {{ status: string, urgency: string, show_in_payments_tab: boolean, label: string }}
 */
export function getPaymentStatus(inquiry, booking) {
  // Default: no payment concern
  const safe = { status: 'none', urgency: 'low', show_in_payments_tab: false, label: '' };

  // Only requests with bookings can have payment issues
  if (!booking) return safe;

  // If booking is cancelled or fully completed with all payments, it's paid/resolved
  if (['cancelled', 'cancelled_by_vendor', 'cancelled_by_customer'].includes(booking.status)) return safe;

  const now = new Date();

  // ── 1. Deposit checks ──
  if (!booking.deposit_paid) {
    // Check if deposit is expired
    const depositDeadline = booking.deposit_due_date
      ? new Date(booking.deposit_due_date)
      : booking.payment_window_expires_at
        ? new Date(booking.payment_window_expires_at)
        : null;

    // Also check server-provided flags
    const isExpiredByServer = booking.payment_window_status === 'expired' || booking.can_pay_deposit === false;

    if (isExpiredByServer || (depositDeadline && now > depositDeadline)) {
      return {
        status: 'deposit_expired',
        urgency: 'high',
        show_in_payments_tab: true,
        label: 'Deposit Expired',
      };
    }

    if (depositDeadline && now <= depositDeadline) {
      return {
        status: 'deposit_required',
        urgency: 'medium',
        show_in_payments_tab: true,
        label: 'Deposit Required',
      };
    }

    // Has booking but deposit not paid and no deadline info — still flag it
    if (booking.status === 'pending_deposit') {
      return {
        status: 'deposit_required',
        urgency: 'medium',
        show_in_payments_tab: true,
        label: 'Deposit Required',
      };
    }
  }

  // ── 2. Final payment checks (deposit is paid) ──
  if (booking.deposit_paid && !booking.final_paid) {
    const finalDueDate = booking.final_payment_due_date ? new Date(booking.final_payment_due_date) : null;
    const eventDate = booking.event_date ? new Date(booking.event_date) : (inquiry.event_date ? new Date(inquiry.event_date) : null);

    // Final payment overdue
    if (finalDueDate && now > finalDueDate) {
      return {
        status: 'final_overdue',
        urgency: 'high',
        show_in_payments_tab: true,
        label: 'Final Payment Overdue',
      };
    }

    // Final payment due soon (event within 7 days)
    if (eventDate) {
      const daysUntilEvent = (eventDate - now) / (1000 * 60 * 60 * 24);
      if (daysUntilEvent <= 7 && daysUntilEvent > 0) {
        return {
          status: 'final_due_soon',
          urgency: 'medium',
          show_in_payments_tab: true,
          label: 'Final Payment Due Soon',
        };
      }
    }
  }

  // ── 3. Fully paid ──
  if (booking.deposit_paid && booking.final_paid) {
    return { status: 'paid', urgency: 'low', show_in_payments_tab: false, label: 'Paid' };
  }

  // Deposit paid, final not yet due
  if (booking.deposit_paid && !booking.final_paid) {
    return { status: 'deposit_paid_final_pending', urgency: 'low', show_in_payments_tab: false, label: 'Deposit Paid' };
  }

  return safe;
}

/**
 * Get the left border color class for a card based on payment status.
 */
export function getPaymentBorderClass(paymentStatus) {
  switch (paymentStatus.urgency) {
    case 'high':
      return 'border-l-red-500';
    case 'medium':
      return 'border-l-amber-400';
    default:
      return null; // Use default card border
  }
}
